import {PureComponent} from "react";
import {DialogManagerProps} from "../../../../../store/dialog-manager/types";
import {connect, DispatchProp} from "react-redux";
import {Overlay} from "../../../overlay/Overlay";
import dialogStyles from "../dialog.module.css";
import {cssHelper} from "../../../../utils/cssHelper";
import icons from "../../../../styles/icons.module.css";
import styles from "./purchase-time-dialog.module.css";
import {IApplicationState} from "../../../../../store";
import React from "react";
import renderText from "../../../renderText";
import {iif} from "../../../iif";
import getFontSize from "../../../../utils/getFontSize";
import {TweenLite, Animation} from "gsap";
import {shakeScale} from "../../../../utils/shake";
import i18n from "../../../../i18n";

interface PurchaseTimeDialogProps {
    readonly coins: number;
    readonly buyMoreTimeCost: number;
    readonly buyMoreTimeCountdown: number;
    readonly rewardedAdIsAvailable: boolean;
}

interface PurchaseTimeDialogState {
    timer: number;
}

class PurchaseTimeDialog extends PureComponent<PurchaseTimeDialogProps & DialogManagerProps & DispatchProp, PurchaseTimeDialogState> {
    private timer?: number;
    private readonly overlay: React.RefObject<HTMLDivElement>;
    private readonly activeTweens: Animation[] = [];

    constructor(props: PurchaseTimeDialogProps & DialogManagerProps & DispatchProp, context: any) {
        super(props, context);

        this.overlay = React.createRef();

        this.state = {
            timer: props.buyMoreTimeCountdown
        };
    }

    render() {
        const {coins, buyMoreTimeCost, rewardedAdIsAvailable} = this.props;
        const fontSize = Math.min(...[
            i18n("gameplay.timepurchase.watchad"),
            i18n("gameplay.timepurchase.buywithcoins")
        ].map(text => getFontSize(text.length, 28, 40, 15, 22)));

        return (
            <Overlay hostElementRef={this.overlay}>
                <div className={dialogStyles.container}>
                    <button className={cssHelper(dialogStyles.close_button, icons.close).toString()} onClick={() => this.props.close()}>✖</button>

                    <figure className={`${dialogStyles.logo} logo`}/>

                    {renderText(i18n("gameplay.timepurchase.header"), 45, 56, 11, 20, styles.header)}
                    {renderText(i18n("gameplay.timepurchase.desc"), 32, 38, 11, 20, styles.header)}

                    <div className={styles.timer}>{this.state.timer}</div>

                    <div className={dialogStyles.vertical_actions}>
                        {iif(rewardedAdIsAvailable,
                            <button className={cssHelper(dialogStyles.button, dialogStyles.primary_button, styles.action).toString()} onClick={() => this.props.close({ value: 'watchAd' })}>
                                <span style={{fontSize}}>{i18n("gameplay.timepurchase.watchad")}</span>
                                <span className={styles.icon}><i className={cssHelper(icons.icon, icons.reward).toString()}/></span>
                            </button>
                        )}

                        <button className={cssHelper(dialogStyles.button, dialogStyles.primary_button, styles.action).toString()} onClick={() => this.props.close({ value: 'buyWithCoins' })} disabled={buyMoreTimeCost > coins}>
                            <span style={{fontSize}}>{i18n("gameplay.timepurchase.buywithcoins")}</span>
                            <span className={cssHelper(styles.icon, styles.cost).toString()}><i className={cssHelper(icons.icon, icons.coins).toString()}/>{buyMoreTimeCost}</span>
                        </button>

                        <button className={cssHelper(dialogStyles.button, dialogStyles.secondary_button, styles.action).toString()} onClick={() => this.props.close()}>
                            <span style={{fontSize: fontSize * 0.9}}>{i18n("gameplay.timepurchase.nothanks")}</span>
                        </button>
                    </div>
                </div>
            </Overlay>
        );
    }

    componentDidMount(): void {
        this.startTimer();

        if (!this.overlay.current || !this.props.animation.enabled)
            return;

        const tween = TweenLite.from(this.overlay.current, 0.33, {
            opacity: 0
        }).timeScale(this.props.animation.timeScale);

        this.activeTweens.push(tween);
    }

    componentDidUpdate(prevProps: Readonly<PurchaseTimeDialogProps & DialogManagerProps & DispatchProp>, prevState: Readonly<PurchaseTimeDialogState>, snapshot?: any): void {
        if (!this.overlay.current || !this.props.animation.enabled)
            return;

        const timer = this.overlay.current.getElementsByClassName(styles.timer)[0];

        if (prevState.timer !== this.state.timer) {
            const tween = shakeScale(timer, 0.4, 5, 0.3).timeScale(this.props.animation.timeScale);

            this.activeTweens.push(tween);
        }
    }

    componentWillUnmount(): void {
        this.stopTimer();

        for (let animation of this.activeTweens) {
            animation.kill();
        }
    }

    private recalculateState(autoSet = false) {
        const timer = this.state.timer - 1;

        if (timer < 1) {
            this.props.close();
        } else if (autoSet) {
            this.setState({ timer });
        }
    }

    private startTimer() {
        this.stopTimer();

        this.timer = window.setInterval(() => this.recalculateState(true), 1000);
    }

    private stopTimer() {
        if (this.timer) {
            clearInterval(this.timer);
        }
    }
}

export default connect(({ player, settings, advertisement }: IApplicationState) => ({
    coins: player.coins,
    buyMoreTimeCost: settings.gameBalance.buyMoreTimeCost,
    buyMoreTimeCountdown: settings.gameBalance.buyMoreTimeCountdown,
    rewardedAdIsAvailable: advertisement.rewardedAdIsAvailable,
    animation: settings.animation,
}))(PurchaseTimeDialog);